import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";
import { useReducedMotion } from "framer-motion";
import { Play, Pause, Maximize2 } from "lucide-react";
import { PROOF_ITEMS } from "@/data/content";
import { Reveal, SectionLabel, CtaButton } from "@/components/landing/shared";

const VideoCard = ({ item, clickAllowed }) => {
  const ref = useRef(null);
  const isVideo = !!item.video;

  const goFullscreen = () => {
    if (clickAllowed && !clickAllowed()) return; // ignore the click that ends a drag
    const v = ref.current;
    if (!v) return;
    v.controls = true;
    const req =
      v.requestFullscreen ||
      v.webkitRequestFullscreen ||
      v.webkitEnterFullscreen ||
      v.msRequestFullscreen;
    if (req) {
      try {
        req.call(v);
      } catch (e) {
        /* fullscreen may be blocked; ignore */
      }
    }
    v.play?.();
  };

  return (
    <div
      className={`group relative mx-3 h-[380px] shrink-0 select-none overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 sm:h-[460px] ${
        isVideo ? "cursor-pointer" : ""
      }`}
      style={{ aspectRatio: item.ratio }}
      data-testid={`proof-card-${item.id}`}
      onClick={isVideo ? goFullscreen : undefined}
      role={isVideo ? "button" : undefined}
    >
      {isVideo ? (
        <video
          ref={ref}
          src={item.video}
          poster={item.img}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="pointer-events-none h-full w-full object-cover"
        />
      ) : (
        <img
          src={item.img}
          alt={item.label}
          draggable={false}
          className="pointer-events-none h-full w-full object-cover opacity-75 grayscale transition-transform duration-700 group-hover:scale-105"
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-black/70 px-2.5 py-1 ff-mono text-[10px] text-white backdrop-blur">
        {item.duration}
      </span>
      {isVideo && (
        <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
          <Maximize2 className="h-5 w-5" />
        </span>
      )}
      <p className="pointer-events-none absolute bottom-4 left-4 ff-mono text-xs uppercase tracking-widest text-zinc-300">
        {item.label}
      </p>
    </div>
  );
};

export const ProofCarousel = () => {
  const reduce = useReducedMotion();

  // Stable references — recreating these each render re-inits embla and breaks drag/click.
  const options = useMemo(
    () => ({ loop: true, dragFree: true, align: "start", containScroll: false }),
    []
  );
  const plugins = useMemo(
    () =>
      reduce
        ? []
        : [AutoScroll({ playOnInit: true, speed: 1, stopOnInteraction: false, stopOnMouseEnter: true })],
    [reduce]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);
  const [playing, setPlaying] = useState(!reduce);

  const clickAllowed = useCallback(() => emblaApi?.clickAllowed?.() ?? true, [emblaApi]);

  const toggle = useCallback(() => {
    const ap = emblaApi?.plugins()?.autoScroll;
    if (!ap) return;
    ap.isPlaying() ? ap.stop() : ap.play();
  }, [emblaApi]);

  useEffect(() => {
    const ap = emblaApi?.plugins()?.autoScroll;
    if (!ap) return;
    const onPlay = () => setPlaying(true);
    const onStop = () => setPlaying(false);
    emblaApi.on("autoScroll:play", onPlay).on("autoScroll:stop", onStop);
    return () => {
      emblaApi.off("autoScroll:play", onPlay).off("autoScroll:stop", onStop);
    };
  }, [emblaApi]);

  const items = [...PROOF_ITEMS, ...PROOF_ITEMS];

  return (
    <section id="proof" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <SectionLabel index="01" testid="proof-label">
            Proof
          </SectionLabel>
          <div className="mt-6 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <h2 className="font-display text-3xl font-bold leading-none tracking-tight sm:text-5xl">
              Scroll-stoppers, in action.
            </h2>
            <div className="space-y-4">
              <p className="text-base leading-relaxed text-zinc-400">
                We engineer motion ads built to hook audiences inside 1.5 seconds — no camera
                crews, just rapid, high-impact animation for DTC and B2B SaaS brands.
              </p>
              <p className="ff-mono text-sm text-zinc-500">
                “87% of video marketers say video directly increased sales.”
                <span className="text-zinc-600"> — Wyzowl</span>
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <div
        className="mt-14 cursor-grab overflow-hidden active:cursor-grabbing"
        ref={emblaRef}
        data-testid="proof-carousel"
      >
        <div className="flex">
          {items.map((item, idx) => (
            <div className="shrink-0" key={`${item.id}-${idx}`}>
              <VideoCard item={item} clickAllowed={clickAllowed} />
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-10 flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 sm:px-8">
        <button
          onClick={toggle}
          data-testid="proof-play-toggle"
          className="inline-flex items-center gap-2 rounded-full border border-zinc-800 px-4 py-2 ff-mono text-xs uppercase tracking-widest text-zinc-400 transition-colors hover:border-zinc-600 hover:text-white"
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {playing ? "Pause reel" : "Play reel"}
        </button>
        <CtaButton testid="proof-cta" variant="outline">
          Get my free sample
        </CtaButton>
      </div>
    </section>
  );
};
